import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Extrai as iniciais do nome do usuário
 * @param name Nome completo do usuário
 * @returns Iniciais do usuário (primeira letra de cada parte do nome)
 */
export function getUserInitials(name?: string): string {
  if (!name || name.trim() === '') {
    return 'U';
  }

  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  return words
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * Interface para representar um objeto de usuário genérico
 */
interface UserLike {
  given_name?: string;
  family_name?: string;
  name?: string;
  preferred_username?: string;
  email?: string;
}

/**
 * Extrai o nome de exibição do usuário de forma robusta
 * Tenta múltiplos campos em ordem de prioridade
 * @param user Objeto de usuário (pode ser de Keycloak, Azure AD, etc.)
 * @returns Nome de exibição do usuário ou undefined se não encontrado
 */
export function getUserDisplayName(user: UserLike | null | undefined): string | undefined {
  if (!user) return undefined;

  // 1. Tenta given_name + family_name
  const givenName = user.given_name?.trim();
  const familyName = user.family_name?.trim();
  if (givenName || familyName) {
    const fullName = `${givenName || ''} ${familyName || ''}`.trim();
    if (fullName) return fullName;
  }

  // 2. Tenta o campo name direto
  if (user.name && typeof user.name === 'string' && user.name.trim()) {
    return user.name.trim();
  }

  // 3. Tenta preferred_username
  if (user.preferred_username && typeof user.preferred_username === 'string' && user.preferred_username.trim()) {
    return user.preferred_username.trim();
  }

  // 4. Tenta extrair do email (parte antes do @)
  if (user.email && typeof user.email === 'string' && user.email.includes('@')) {
    const emailName = user.email.split('@')[0];
    // Capitaliza primeira letra de cada parte
    return emailName
      .split(/[._-]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  return undefined;
}
